import { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import {
  MessageCircle,
  Send,
  Plus,
  Users,
  Edit2,
  X,
  ArrowLeft,
  Phone,
  Video,
  MoreVertical,
  Search,
  Bell,
  Paperclip,
  Smile,
  Image as ImageIcon,
  FileText,
  Trash2,
  User,
  UserPlus,
  Calendar,
  CheckSquare,
  Download,
  ChevronRight,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { DOC_KINDS, buildDocAttachment, DocMessage, FileMessage, PrintJobMessage, PlanningMessage } from '../components/echange/DocMessages';

const palette = [
  'bg-blue-500',
  'bg-emerald-500',
  'bg-violet-500',
  'bg-orange-500',
  'bg-rose-500',
  'bg-teal-500',
  'bg-indigo-500',
  'bg-amber-500',
  'bg-pink-500',
];

const Avatar = ({ name = '?', size = 'md', online = false }) => {
  const initial = name.trim().charAt(0).toUpperCase();
  const idx = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % palette.length;
  const sizeClass = size === 'lg' ? 'w-14 h-14 text-xl' : size === 'sm' ? 'w-8 h-8 text-xs' : 'w-11 h-11 text-base';
  return (
    <div className="relative flex-shrink-0">
      <div className={`${sizeClass} rounded-full ${palette[idx]} text-white font-bold flex items-center justify-center shadow-sm`}>
        {initial}
      </div>
      {online && (
        <span className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full"></span>
      )}
    </div>
  );
};

const formatTime = (ts) => {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return 'Hier';
    const diff = (now - d) / 86400000;
    if (diff < 7) {
      return d.toLocaleDateString('fr-FR', { weekday: 'short' }).replace('.', '');
    }
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
  } catch {
    return '';
  }
};

const getOtherParticipantEmail = (conv, currentUserEmail) => {
  if (!Array.isArray(conv.participants)) return null;
  return conv.participants.find((p) => p !== currentUserEmail) || null;
};

const getOtherParticipantName = (conv, currentUserEmail, employeesByEmail = {}) => {
  if (conv.type === 'group') return conv.name || 'Groupe';
  // 1) Utiliser participants_details si fourni par le backend
  if (Array.isArray(conv.participants_details)) {
    const other = conv.participants_details.find((p) => p.email !== currentUserEmail);
    if (other?.name) return other.name;
    if (other?.email) return other.email;
  }
  // 2) Sinon résoudre via la liste des employés
  const otherEmail = getOtherParticipantEmail(conv, currentUserEmail);
  if (otherEmail) {
    const emp = employeesByEmail[otherEmail];
    if (emp?.name) return emp.name;
    return otherEmail;
  }
  return conv.name || 'Conversation';
};

const getRole = (conv, currentUserEmail, employeesByEmail = {}) => {
  if (conv.type === 'group') return `${(conv.participants || []).length} participants`;
  if (Array.isArray(conv.participants_details)) {
    const other = conv.participants_details.find((p) => p.email !== currentUserEmail);
    if (other?.role || other?.position) return other.role || other.position;
  }
  const otherEmail = getOtherParticipantEmail(conv, currentUserEmail);
  if (otherEmail) {
    const emp = employeesByEmail[otherEmail];
    if (emp?.role || emp?.position) return emp.role || emp.position;
    return otherEmail;
  }
  return '';
};

export default function Echange({ user, onLogout }) {
  const [conversations, setConversations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [conversationType, setConversationType] = useState('individual');
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newConversationName, setNewConversationName] = useState('');
  const [showDetails, setShowDetails] = useState(true);
  const [tab, setTab] = useState('conversations'); // conversations | groupes | non_lus
  const [search, setSearch] = useState('');
  const messagesEndRef = useRef(null);
  const [sharedStats, setSharedStats] = useState({ contracts: 0, prospects: 0, plannings: 0, files: 0, devis: 0, factures: 0, tasks: 0 });
  const [showAttachPicker, setShowAttachPicker] = useState(false);
  const [showPickerModal, setShowPickerModal] = useState(null); // 'contract'|'prospect'|'planning'|'task'|'devis'|'facture'|'reservation'
  const [pickerItems, setPickerItems] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  useEffect(() => {
    loadConversations();
    loadEmployees();
    const interval = setInterval(() => {
      loadConversations();
      if (selectedConversation) loadMessages(selectedConversation.id, false);
    }, 2000);
    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, [selectedConversation]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversations = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/conversations`);
      setConversations(r.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/all`);
      // On garde TOUS les utilisateurs (y compris current) pour pouvoir résoudre les noms
      setEmployees(r.data || []);
    } catch (e) {
      console.error(e);
    }
  };

  const loadMessages = async (id, showToast = true) => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${id}/messages`);
      setMessages(r.data || []);
    } catch (e) {
      if (showToast) toast.error('Erreur lors du chargement');
    }
  };

  const handleSelect = (c) => {
    setSelectedConversation(c);
    loadMessages(c.id);
    loadSharedStats(c.id);
  };

  const loadSharedStats = async (convId) => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${convId}/shared-stats`);
      setSharedStats(r.data || {});
    } catch (e) { setSharedStats({ contracts: 0, prospects: 0, plannings: 0, files: 0, devis: 0, factures: 0, tasks: 0 }); }
  };

  // Charge la liste d'éléments proposés dans le picker (documents / prospects)
  const openPicker = async (kind) => {
    setShowAttachPicker(false);
    setShowPickerModal(kind);
    if (kind === 'task') return;
    setPickerLoading(true);
    try {
      const path = kind === 'prospect' ? '/api/prospects' : DOC_KINDS[kind].list;
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${path}`);
      setPickerItems(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    } catch (e) {
      toast.error("Impossible de charger la liste");
      setPickerItems([]);
    } finally { setPickerLoading(false); }
  };

  // Envoie un message typé (attachment) dans la conversation
  const sendTypedMessage = async (type, attachment, preview = '') => {
    if (!selectedConversation) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/conversations/${selectedConversation.id}/messages`,
        { type, attachment, content: preview }
      );
      setShowPickerModal(null);
      loadMessages(selectedConversation.id, false);
      loadConversations();
      loadSharedStats(selectedConversation.id);
      toast.success('Envoyé');
    } catch (e) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  // Partage d'un document CRM (contrat, devis, facture, réservation, bon/liste de commande…) en PDF
  const shareDoc = (kind, item) => sendTypedMessage(kind, buildDocAttachment(kind, item));

  // Envoi d'un fichier depuis l'ordinateur
  const uploadFile = async (file) => {
    if (!file || !selectedConversation) return;
    setUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${selectedConversation.id}/files`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadMessages(selectedConversation.id, false);
      loadConversations();
      loadSharedStats(selectedConversation.id);
      toast.success('Fichier envoyé');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi du fichier impossible");
    } finally {
      setUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Attribution d'un prospect : patche + envoie un message typé
  const assignProspect = async (prospect) => {
    const otherEmail = getOtherParticipantEmail(selectedConversation, user.email);
    if (!otherEmail) { toast.error("Pas de destinataire"); return; }
    try {
      await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/prospects/${prospect.id}`, {
        ...prospect,
        assigned_to: otherEmail,
      });
      await sendTypedMessage('prospect_assigned', {
        prospect_ids: [prospect.id],
        prospects: [{
          id: prospect.id,
          entreprise: prospect.entreprise || prospect.nom || '—',
          secteur: prospect.secteur || '',
          ville: prospect.ville || prospect.adresse || '',
          temperature: prospect.temperature || 'Tiède',
        }],
        assigned_to: otherEmail,
      });
      toast.success(`Prospect attribué à ${otherEmail}`);
    } catch (e) {
      toast.error("Attribution impossible");
    }
  };

  const otherParticipants = () => (selectedConversation?.participants || []).filter((p) => p.toLowerCase() !== user.email.toLowerCase());

  // Partage le planning de la semaine (interlocuteur si visible, sinon le mien)
  const sharePlanning = async () => {
    setShowAttachPicker(false);
    const now = new Date();
    const monday = new Date(now); monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sunday = new Date(monday); sunday.setDate(monday.getDate() + 6);
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/planning`, { params: { date_from: iso(monday), date_to: iso(sunday) } });
      const all = r.data || [];
      const others = otherParticipants().map((e) => e.toLowerCase());
      const forOthers = all.filter((a) => others.includes((a.user_email || '').toLowerCase()));
      const list = selectedConversation?.type === 'individual' && forOthers.length ? forOthers : all;
      const ownerEmail = selectedConversation?.type === 'individual' && forOthers.length ? otherParticipants()[0] : user.email;
      const owner = user.role === 'admin' && list === all ? 'Équipe SESAM' : (employeesByEmail[ownerEmail]?.name || ownerEmail);
      const items = list
        .sort((a, b) => `${a.date} ${a.heure_debut}`.localeCompare(`${b.date} ${b.heure_debut}`))
        .slice(0, 30)
        .map((a) => ({
          day: new Date(`${a.date}T00:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit' }),
          time: `${a.heure_debut || ''}${a.heure_fin ? `–${a.heure_fin}` : ''}`,
          title: a.titre || a.type_label || 'Activité',
          who: list === all ? a.user_name : '',
          color: a.type_color || '#3b82f6',
        }));
      const week = Math.ceil(((now - new Date(now.getFullYear(), 0, 1)) / 86400000 + 1) / 7);
      await sendTypedMessage('planning', { week_number: week, year: now.getFullYear(), date_from: iso(monday), date_to: iso(sunday), owner, items });
    } catch (e) {
      toast.error("Impossible de récupérer le planning");
    }
  };

  // Assigner une tâche : crée l'activité dans le Planning de la personne + message
  const createTask = async ({ title, date, heure, assignee, description }) => {
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/planning`, {
        user_email: assignee,
        titre: title,
        type: 'tache',
        date,
        heure_debut: heure,
        heure_fin: `${String(Math.min(23, parseInt(heure.slice(0, 2), 10) + 1)).padStart(2, '0')}:${heure.slice(3, 5)}`,
        description,
      });
      await sendTypedMessage('task', {
        planning_id: r.data?.id || null,
        title,
        date,
        heure,
        description,
        assignee,
        assignee_name: employeesByEmail[assignee]?.name || assignee,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de créer la tâche");
    }
  };

  // Supprime la conversation courante
  const deleteConversation = async () => {
    if (!selectedConversation) return;
    if (!window.confirm("Supprimer définitivement cette conversation et tous ses messages ?")) return;
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${selectedConversation.id}`);
      toast.success("Conversation supprimée");
      setSelectedConversation(null);
      setMessages([]);
      loadConversations();
    } catch (e) {
      toast.error("Suppression impossible");
    }
  };

  const handleCreate = async () => {
    if (conversationType === 'individual' && selectedEmployees.length !== 1) {
      toast.error('Sélectionnez un employé');
      return;
    }
    if (conversationType === 'group') {
      if (selectedEmployees.length < 2) return toast.error('Au moins 2 participants');
      if (!groupName.trim()) return toast.error('Nom de groupe requis');
    }
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/conversations`, {
        type: conversationType,
        participants: selectedEmployees,
        name: conversationType === 'group' ? groupName : null,
      });
      toast.success('Conversation créée');
      setShowCreateModal(false);
      setSelectedEmployees([]);
      setGroupName('');
      setConversationType('individual');
      loadConversations();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleRename = async () => {
    if (!newConversationName.trim()) return toast.error('Nom requis');
    try {
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/conversations/${selectedConversation.id}/rename`,
        { name: newConversationName }
      );
      toast.success('Conversation renommée');
      setShowRenameModal(false);
      setNewConversationName('');
      loadConversations();
      setSelectedConversation({ ...selectedConversation, name: newConversationName });
    } catch (e) {
      toast.error('Erreur');
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/conversations/${selectedConversation.id}/messages`,
        { content: newMessage }
      );
      setNewMessage('');
      loadMessages(selectedConversation.id, false);
      loadConversations();
    } catch (e) {
      toast.error("Erreur lors de l'envoi");
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleEmp = (email) => {
    setSelectedEmployees((s) => (s.includes(email) ? s.filter((x) => x !== email) : [...s, email]));
  };

  // Audio / Video call via Jitsi Meet
  const startCall = (videoEnabled) => {
    if (!selectedConversation) return;
    const slug = `sesam-crm-${selectedConversation.id}`.replace(/[^a-zA-Z0-9-]/g, '');
    const params = new URLSearchParams({
      'config.startWithAudioMuted': 'false',
      'config.startWithVideoMuted': videoEnabled ? 'false' : 'true',
      'config.prejoinPageEnabled': 'false',
      'userInfo.displayName': user.name || user.email,
    });
    const url = `https://meet.jit.si/${slug}#${params.toString()}`;
    window.open(url, '_blank', 'noopener,noreferrer,width=1200,height=800');
    // Optionnel : envoyer un message d'invitation dans la conversation
    if (selectedConversation) {
      axios
        .post(
          `${process.env.REACT_APP_BACKEND_URL}/api/conversations/${selectedConversation.id}/messages`,
          {
            content: `${videoEnabled ? '📹 Appel vidéo' : '📞 Appel audio'} démarré — Rejoindre : ${url}`,
          }
        )
        .then(() => loadMessages(selectedConversation.id, false))
        .catch(() => {});
    }
    toast.success(`${videoEnabled ? 'Appel vidéo' : 'Appel audio'} lancé`);
  };

  // Map email → user details (insensible à la casse)
  const employeesByEmail = useMemo(() => {
    const map = {};
    employees.forEach((e) => {
      if (e?.email) map[e.email.toLowerCase()] = e;
    });
    return new Proxy(map, {
      get: (t, p) => (typeof p === 'string' ? t[p.toLowerCase()] : t[p]),
    });
  }, [employees]);

  // Filtrage
  const filteredConversations = useMemo(() => {
    let list = conversations.filter((c) => {
      const name = getOtherParticipantName(c, user.email, employeesByEmail).toLowerCase();
      return !search || name.includes(search.toLowerCase());
    });
    if (tab === 'groupes') list = list.filter((c) => c.type === 'group');
    else if (tab === 'non_lus') list = list.filter((c) => (c.unread_count || 0) > 0);
    return list;
  }, [conversations, search, tab, user.email, employeesByEmail]);

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
  const totalGroups = conversations.filter((c) => c.type === 'group').length;

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="echange">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="echange">
      <div className="w-full px-4 sm:px-6 py-4" data-testid="messagerie-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <MessageCircle className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1C2A46]">Messagerie</h1>
              <p className="text-xs text-slate-500">Communiquez facilement avec vos collaborateurs.</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-80 max-w-full hidden md:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Rechercher une conversation..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-10 bg-white"
                data-testid="messagerie-search"
              />
            </div>
            <button className="relative w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center hover:bg-slate-50">
              <Bell className="h-4 w-4 text-slate-600" />
              {totalUnread > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                  {totalUnread > 9 ? '9+' : totalUnread}
                </span>
              )}
            </button>
            <Button
              className="gap-2 h-10 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setShowCreateModal(true)}
              data-testid="messagerie-new-btn"
            >
              <Edit2 className="h-4 w-4" />
              Nouveau message
            </Button>
          </div>
        </div>

        {/* 3-column layout */}
        <div className="grid grid-cols-12 gap-4 h-[calc(100vh-220px)] min-h-[600px]">
          {/* SIDEBAR LEFT */}
          <div className="col-span-12 lg:col-span-3 bg-white rounded-xl shadow-sm flex flex-col overflow-hidden">
            {/* Tabs */}
            <div className="border-b border-slate-200 px-4 pt-2">
              <div className="flex gap-4 text-sm">
                <TabButton id="conversations" current={tab} setTab={setTab} label="Conversations" />
                <TabButton id="groupes" current={tab} setTab={setTab} label="Groupes" badge={totalGroups} />
                <TabButton id="non_lus" current={tab} setTab={setTab} label="Non lus" badge={totalUnread} highlight />
              </div>
            </div>

            {/* Search mobile */}
            <div className="md:hidden p-3 border-b border-slate-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
            </div>

            {/* Conversations list */}
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="p-6 text-center text-slate-400 text-sm">
                  <MessageCircle className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  Aucune conversation
                </div>
              ) : (
                filteredConversations.map((c) => {
                  const name = getOtherParticipantName(c, user.email, employeesByEmail);
                  const isActive = selectedConversation?.id === c.id;
                  const isGroup = c.type === 'group';
                  return (
                    <button
                      key={c.id}
                      onClick={() => handleSelect(c)}
                      className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex items-start gap-3 ${
                        isActive ? 'bg-blue-50' : ''
                      }`}
                      data-testid={`conv-${c.id}`}
                    >
                      {isGroup ? (
                        <div className="w-11 h-11 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
                          <Users className="h-5 w-5" />
                        </div>
                      ) : (
                        <Avatar name={name} online={c.online} />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-[#1C2A46] truncate">{name}</p>
                          <span className="text-[11px] text-slate-400 flex-shrink-0">
                            {formatTime(c.last_message_at || c.updated_at)}
                          </span>
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <p className="text-xs text-slate-500 truncate">
                            {c.last_message_preview || 'Démarrer la conversation'}
                          </p>
                          {(c.unread_count || 0) > 0 && (
                            <span className="bg-blue-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center flex-shrink-0">
                              {c.unread_count > 9 ? '9+' : c.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* CHAT MIDDLE */}
          <div className={`col-span-12 ${showDetails ? 'lg:col-span-6' : 'lg:col-span-9'} bg-white rounded-xl shadow-sm flex flex-col overflow-hidden`}>
            {!selectedConversation ? (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-center p-8">
                <div>
                  <MessageCircle className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">Sélectionnez une conversation pour commencer</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
                  <div className="flex items-center gap-3 min-w-0">
                    <button
                      className="lg:hidden w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center flex-shrink-0"
                      onClick={() => setSelectedConversation(null)}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </button>
                    {selectedConversation.type === 'group' ? (
                      <div className="w-11 h-11 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
                        <Users className="h-5 w-5" />
                      </div>
                    ) : (
                      <Avatar
                        name={getOtherParticipantName(selectedConversation, user.email, employeesByEmail)}
                        online
                      />
                    )}
                    <div className="min-w-0">
                      <h2 className="text-base font-bold text-[#1C2A46] truncate">
                        {getOtherParticipantName(selectedConversation, user.email, employeesByEmail)}
                      </h2>
                      <p className="text-xs text-slate-500">
                        {getRole(selectedConversation, user.email, employeesByEmail)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <CallButton onClick={() => startCall(false)} title="Appel audio" testid="audio-call-btn">
                      <Phone className="h-4 w-4" />
                    </CallButton>
                    <CallButton onClick={() => startCall(true)} title="Appel vidéo" testid="video-call-btn">
                      <Video className="h-4 w-4" />
                    </CallButton>
                    <CallButton onClick={() => setShowDetails((v) => !v)} title="Détails">
                      <MoreVertical className="h-4 w-4" />
                    </CallButton>
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-slate-50/30">
                  <div className="flex justify-center">
                    <span className="px-3 py-1 bg-slate-100 text-slate-500 text-xs rounded-full">
                      Aujourd'hui
                    </span>
                  </div>
                  {messages.length === 0 ? (
                    <div className="text-center text-slate-400 text-sm py-8">
                      Aucun message — soyez le premier à écrire !
                    </div>
                  ) : (
                    messages.map((m, i) => {
                      const isMe = m.sender_email === user.email;
                      const msgType = m.type || 'text';
                      if (msgType !== 'text' && m.attachment) {
                        return (
                          <div key={m.id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                            <RichMessage msg={m} isMe={isMe} />
                          </div>
                        );
                      }
                      return (
                        <div key={m.id || i} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[70%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${
                              isMe
                                ? 'bg-blue-600 text-white rounded-tr-sm'
                                : 'bg-white text-slate-800 rounded-tl-sm border border-slate-100'
                            }`}
                          >
                            {!isMe && selectedConversation.type === 'group' && (
                              <p className="text-[11px] font-semibold text-blue-600 mb-0.5">
                                {m.sender_name || m.sender_email}
                              </p>
                            )}
                            <p className="whitespace-pre-wrap break-words">{m.content}</p>
                            <p className={`text-[10px] mt-1 ${isMe ? 'text-blue-100' : 'text-slate-400'} text-right`}>
                              {formatTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef}></div>
                </div>

                {/* Input */}
                <div className="border-t border-slate-100 px-4 py-3 bg-white relative">
                  {showAttachPicker && (
                    <div className="absolute bottom-16 left-4 w-80 bg-white border border-slate-200 shadow-xl rounded-xl overflow-hidden z-30" data-testid="attach-picker">
                      <div className="px-4 py-2 border-b border-slate-100 text-xs font-bold text-slate-500 uppercase tracking-wide">
                        Joindre à la conversation
                      </div>
                      <div className="max-h-[60vh] overflow-y-auto">
                        <AttachOption Icon={Calendar} color="bg-amber-500" title="Planning" desc="Partager le planning de la semaine" onClick={sharePlanning} testid="attach-planning" />
                        <AttachOption Icon={CheckSquare} color="bg-violet-500" title="Tâche" desc="Assigner une tâche avec échéance" onClick={() => openPicker('task')} testid="attach-task" />
                        {user.role !== 'technicien' && <AttachOption Icon={UserPlus} color="bg-emerald-500" title="Prospect attribué" desc="Notifier l'attribution d'un prospect" onClick={() => openPicker('prospect')} testid="attach-prospect" />}
                        {user.role !== 'technicien' && Object.entries(DOC_KINDS).map(([k, d]) => (
                          <AttachOption key={k} Icon={FileText} color={ATTACH_COLORS[d.color]} title={d.label} desc={`Envoyer en PDF`} onClick={() => openPicker(k)} testid={`attach-${k}`} />
                        ))}
                        <AttachOption Icon={Paperclip} color="bg-slate-500" title="Fichier" desc="Image, PDF, document depuis l'ordinateur" onClick={() => { setShowAttachPicker(false); fileInputRef.current?.click(); }} testid="attach-file" />
                      </div>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <button
                      onClick={() => setShowAttachPicker(v => !v)}
                      className={`w-9 h-9 rounded-full flex items-center justify-center text-white flex-shrink-0 transition ${showAttachPicker ? 'bg-blue-700 rotate-45' : 'bg-blue-600 hover:bg-blue-700'}`}
                      title="Joindre"
                      data-testid="btn-attach"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                    <input ref={fileInputRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="message-file-input" />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingFile}
                      className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0 disabled:opacity-50"
                      title="Joindre un fichier"
                      data-testid="btn-attach-file"
                    >
                      <Paperclip className={`h-4 w-4 ${uploadingFile ? 'animate-pulse' : ''}`} />
                    </button>
                    <Input
                      placeholder="Écrire un message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyPress={handleKey}
                      className="flex-1 h-10 bg-slate-50 border-slate-200"
                      data-testid="message-input"
                    />
                    <Button
                      onClick={handleSend}
                      disabled={!newMessage.trim()}
                      className="h-10 w-10 p-0 bg-blue-600 hover:bg-blue-700 rounded-full"
                      data-testid="send-message-btn"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* DETAILS RIGHT */}
          {showDetails && selectedConversation && (
            <div className="hidden lg:flex col-span-3 bg-white rounded-xl shadow-sm flex-col overflow-y-auto">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <h3 className="text-sm font-bold text-[#1C2A46]">Détails de la conversation</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="w-7 h-7 rounded-full hover:bg-slate-100 flex items-center justify-center"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Profil */}
              <div className="p-4 border-b border-slate-100">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  {selectedConversation.type === 'group' ? (
                    <div className="w-14 h-14 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6" />
                    </div>
                  ) : (
                    <Avatar
                      name={getOtherParticipantName(selectedConversation, user.email, employeesByEmail)}
                      size="lg"
                      online
                    />
                  )}
                  <div className="min-w-0">
                    <h4 className="text-sm font-bold text-[#1C2A46] truncate">
                      {getOtherParticipantName(selectedConversation, user.email, employeesByEmail)}
                    </h4>
                    <p className="text-xs text-slate-500 truncate">
                      {getRole(selectedConversation, user.email, employeesByEmail)}
                    </p>
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 mt-0.5">
                      <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
                      En ligne
                    </span>
                  </div>
                </div>

                {/* Quick actions */}
                <div className="grid grid-cols-4 gap-2 mt-4">
                  <QuickAction Icon={Phone} label="Appel audio" onClick={() => startCall(false)} />
                  <QuickAction Icon={Video} label="Appel vidéo" onClick={() => startCall(true)} />
                  <QuickAction Icon={User} label="Profil" />
                  <QuickAction Icon={MoreVertical} label="Plus" />
                </div>
              </div>

              {/* Envoyer rapidement */}
              <div className="p-4 border-b border-slate-100">
                <h4 className="text-sm font-bold text-[#1C2A46] mb-3">Envoyer rapidement</h4>
                <div className="space-y-2">
                  {user.role !== 'technicien' && <QuickSend Icon={FileText}  label="Envoyer un contrat"      onClick={() => openPicker('contract')} testid="quick-contract" />}
                  {user.role !== 'technicien' && <QuickSend Icon={UserPlus}  label="Attribuer un prospect"   onClick={() => openPicker('prospect')} testid="quick-prospect" />}
                  <QuickSend Icon={Calendar}  label="Partager le planning"    onClick={sharePlanning}                testid="quick-planning" />
                  <QuickSend Icon={CheckSquare} label="Assigner une tâche"    onClick={() => openPicker('task')}     testid="quick-task" />
                  {user.role !== 'technicien' && <QuickSend Icon={FileText}  label="Envoyer un devis"        onClick={() => openPicker('devis')}    testid="quick-devis" />}
                  {user.role !== 'technicien' && <QuickSend Icon={FileText}  label="Envoyer une facture"     onClick={() => openPicker('facture')}  testid="quick-facture" />}
                  {user.role !== 'technicien' && <QuickSend Icon={FileText}  label="Envoyer une réservation" onClick={() => openPicker('reservation')} testid="quick-reservation" />}
                </div>
              </div>

              {/* Éléments partagés */}
              <div className="p-4 border-b border-slate-100">
                <h4 className="text-sm font-bold text-[#1C2A46] mb-3">Éléments partagés</h4>
                <div className="grid grid-cols-2 gap-2">
                  <SharedStatCell n={sharedStats.contracts} label="Contrats"  />
                  <SharedStatCell n={sharedStats.prospects} label="Prospects" />
                  <SharedStatCell n={sharedStats.plannings} label="Plannings" />
                  <SharedStatCell n={sharedStats.files + (sharedStats.devis||0) + (sharedStats.factures||0)} label="Fichiers" />
                </div>
              </div>

              {/* Options */}
              <div className="p-4">
                <button
                  className="w-full flex items-center gap-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 px-3 py-2 rounded-md transition-colors"
                  onClick={deleteConversation}
                  data-testid="btn-delete-conversation"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer la conversation
                </button>
                {selectedConversation.type === 'group' && (
                  <button
                    className="w-full flex items-center gap-2 text-sm text-slate-700 hover:bg-slate-50 px-3 py-2 rounded-md transition-colors mt-1"
                    onClick={() => {
                      setNewConversationName(selectedConversation.name || '');
                      setShowRenameModal(true);
                    }}
                  >
                    <Edit2 className="h-4 w-4" />
                    Renommer le groupe
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal Nouveau message */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-md" data-testid="new-conv-modal">
          <DialogHeader>
            <DialogTitle>Nouveau message</DialogTitle>
            <DialogDescription>Démarrez une conversation individuelle ou un groupe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="flex gap-2">
              <button
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  conversationType === 'individual' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                }`}
                onClick={() => setConversationType('individual')}
              >
                Individuel
              </button>
              <button
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  conversationType === 'group' ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'
                }`}
                onClick={() => setConversationType('group')}
              >
                Groupe
              </button>
            </div>
            {conversationType === 'group' && (
              <div>
                <Label>Nom du groupe</Label>
                <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Ex: Équipe Commerciale" />
              </div>
            )}
            <div>
              <Label>Sélectionner {conversationType === 'group' ? 'des participants' : 'un employé'}</Label>
              <div className="max-h-60 overflow-y-auto mt-2 border border-slate-200 rounded-md divide-y">
                {employees
                  .filter((e) => e.email !== user.email)
                  .map((e) => (
                  <label
                    key={e.email}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedEmployees.includes(e.email)}
                      onCheckedChange={() => toggleEmp(e.email)}
                    />
                    <Avatar name={e.name || e.email} size="sm" />
                    <span className="text-sm">{e.name || e.email}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2 border-t">
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate}>
                Créer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal Renommer */}
      <Dialog open={showRenameModal} onOpenChange={setShowRenameModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Renommer la conversation</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            <Input
              value={newConversationName}
              onChange={(e) => setNewConversationName(e.target.value)}
              placeholder="Nouveau nom"
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowRenameModal(false)}>
                Annuler
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleRename}>
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal : Picker (Contrat / Prospect / Devis / Facture / Reservation / Task) */}
      <Dialog open={!!showPickerModal} onOpenChange={() => setShowPickerModal(null)}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="picker-modal">
          <DialogHeader>
            <DialogTitle>
              {showPickerModal === 'prospect' && 'Attribuer un prospect'}
              {showPickerModal === 'task' && 'Assigner une tâche'}
              {DOC_KINDS[showPickerModal] && `Envoyer : ${DOC_KINDS[showPickerModal].label}`}
            </DialogTitle>
            <DialogDescription>
              {showPickerModal === 'prospect' ? "Le prospect sera attribué à votre interlocuteur."
                : showPickerModal === 'task' ? "La tâche est ajoutée au planning de la personne choisie."
                : "Le document arrive en PDF dans la conversation."}
            </DialogDescription>
          </DialogHeader>

          {showPickerModal === 'task' ? (
            <TaskPickerForm
              participants={otherParticipants().map((e) => ({ email: e, name: employeesByEmail[e]?.name || e }))}
              onSubmit={createTask}
            />
          ) : (
            <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-1 border-t border-slate-100 pt-2">
              {pickerLoading ? (
                <div className="py-8 text-center text-sm text-slate-400">Chargement…</div>
              ) : pickerItems.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-400">Aucun élément trouvé</div>
              ) : (
                pickerItems.map((it, i) => (
                  <PickerRow
                    key={it.id || i}
                    kind={showPickerModal}
                    item={it}
                    onPick={() => (showPickerModal === 'prospect' ? assignProspect(it) : shareDoc(showPickerModal, it))}
                  />
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Composants rich-message et sidebar
// ═══════════════════════════════════════════════════════════════════

const fmtFCFA = (n) => {
  const v = Math.round(Number(n) || 0);
  return v.toLocaleString('fr-FR').replace(/,/g, ' ') + ' FCFA';
};

const ATTACH_COLORS = { blue: 'bg-blue-500', cyan: 'bg-cyan-500', orange: 'bg-orange-500', rose: 'bg-rose-500', indigo: 'bg-indigo-500', teal: 'bg-teal-500', emerald: 'bg-emerald-600' };

function RichMessage({ msg, isMe }) {
  const a = msg.attachment || {};
  const t = msg.type;
  const timeStr = (() => {
    try { return new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  })();

  if (DOC_KINDS[t]) return <DocMessage msg={msg} />;
  if (t === 'file') return <FileMessage msg={msg} />;
  if (t === 'print_job') return <PrintJobMessage msg={msg} />;
  if (t === 'planning') return <PlanningMessage msg={msg} />;

  if (t === 'prospect_assigned') {
    const prospects = a.prospects || [];
    return (
      <div className="max-w-[85%] bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-sm shadow-sm" data-testid="rich-prospect">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <UserPlus className="h-4 w-4" />
          </div>
          <div className="font-bold text-emerald-900 flex-1">{prospects.length} nouveau{prospects.length > 1 ? 'x prospects attribués' : ' prospect attribué'}</div>
          <span className="text-[10px] text-slate-400">{timeStr}</span>
        </div>
        <div className="space-y-2">
          {prospects.map((p, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5">
              <div className="w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center text-xs">
                {(p.entreprise || '?').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-bold text-slate-900 text-xs truncate">{p.entreprise}</div>
                <div className="text-[11px] text-slate-500 truncate">{[p.secteur, p.ville].filter(Boolean).join(' · ')}</div>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{p.temperature}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-3">
          <a href="/prospects" className="flex-1 h-8 px-3 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md flex items-center justify-center">Voir mes prospects</a>
          <button className="h-8 px-3 bg-white border border-slate-300 text-slate-700 text-xs font-semibold rounded-md">Accepter</button>
        </div>
      </div>
    );
  }

  if (t === 'task') {
    return (
      <div className="max-w-[85%] bg-violet-50 border border-violet-200 rounded-2xl p-3 text-sm shadow-sm" data-testid="rich-task">
        <div className="flex items-center gap-2 mb-1.5">
          <div className="w-7 h-7 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center">
            <CheckSquare className="h-4 w-4" />
          </div>
          <div className="font-bold text-violet-900 flex-1 text-xs">Nouvelle tâche</div>
          <span className="text-[10px] text-slate-400">{timeStr}</span>
        </div>
        <div className="font-bold text-slate-900 text-sm">{a.title}</div>
        {a.description && <div className="text-xs text-slate-600 mt-0.5">{a.description}</div>}
        <div className="text-[11px] text-slate-500 mt-1">Échéance : {a.date ? new Date(`${a.date}T00:00:00`).toLocaleDateString('fr-FR') : '—'}{a.heure ? ` à ${a.heure}` : ''} · Assignée à {a.assignee_name || a.assignee}</div>
        <a href="/planning" className="mt-2 inline-flex h-7 px-3 bg-violet-600 hover:bg-violet-700 text-white text-[11px] font-semibold rounded-md items-center" data-testid="btn-task-open-planning">Voir dans le planning</a>
      </div>
    );
  }

  return null;
}

function AttachOption({ Icon, color, title, desc, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left transition-colors"
      data-testid={testid}
    >
      <div className={`w-9 h-9 rounded-lg ${color} text-white flex items-center justify-center flex-shrink-0`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-slate-900">{title}</div>
        <div className="text-[11px] text-slate-500 truncate">{desc}</div>
      </div>
    </button>
  );
}

function QuickSend({ Icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-3 py-2.5 bg-white border border-slate-200 hover:border-blue-300 hover:bg-blue-50/50 rounded-lg transition-colors text-left"
      data-testid={testid}
    >
      <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center flex-shrink-0">
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-sm font-semibold text-blue-700 flex-1">{label}</span>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );
}

function SharedStatCell({ n, label }) {
  return (
    <div className="bg-slate-50 rounded-lg p-3">
      <div className="text-2xl font-black text-slate-900 leading-none">{n || 0}</div>
      <div className="text-[11px] text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function PickerRow({ kind, item, onPick }) {
  let title, subtitle, right;
  // Le Dialog reste monté pendant l'animation de fermeture (kind = null) → éviter le crash
  if (kind !== 'prospect' && !DOC_KINDS[kind]) return null;
  if (kind === 'prospect') {
    title = item.entreprise || item.nom || '—';
    subtitle = [item.secteur, item.ville || item.adresse].filter(Boolean).join(' · ') || item.email || '';
    right = <span className="text-[10px] px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{item.temperature || item.statut || 'Prospect'}</span>;
  } else {
    const n = DOC_KINDS[kind].norm(item);
    title = n.client_nom;
    subtitle = `${DOC_KINDS[kind].label} ${n.numero}`;
    right = n.montant ? fmtFCFA(n.montant) : (kind === 'liste_commande' ? `${item.total_quantite || 0} art.` : '');
  }
  return (
    <button onClick={onPick} className="w-full flex items-center gap-3 px-3 py-3 border-b border-slate-100 hover:bg-blue-50 transition-colors text-left" data-testid={`picker-row-${item.id}`}>
      <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center flex-shrink-0">
        <FileText className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-slate-900 truncate">{title}</div>
        <div className="text-xs text-slate-500 truncate">{subtitle}</div>
      </div>
      <div className="text-xs font-semibold text-slate-700">{right}</div>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );
}

function TaskPickerForm({ participants, onSubmit }) {
  const [title, setTitle] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [heure, setHeure] = useState('09:00');
  const [description, setDescription] = useState('');
  const [assignee, setAssignee] = useState(participants[0]?.email || '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim() || !assignee) return toast.error('Titre et destinataire requis');
    setBusy(true);
    await onSubmit({ title: title.trim(), date, heure, assignee, description: description.trim() });
    setBusy(false);
  };
  return (
    <div className="space-y-3 pt-2">
      <div>
        <Label>Assigner à</Label>
        <select value={assignee} onChange={(e) => setAssignee(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="task-assignee-select">
          {participants.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
        </select>
      </div>
      <div>
        <Label>Titre de la tâche</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ex: Appeler le client X" data-testid="task-title-input" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Échéance</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="task-date-input" />
        </div>
        <div>
          <Label>Heure</Label>
          <Input type="time" value={heure} onChange={(e) => setHeure(e.target.value)} data-testid="task-time-input" />
        </div>
      </div>
      <div>
        <Label>Détails (facultatif)</Label>
        <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Précisions pour la personne" data-testid="task-desc-input" />
      </div>
      <div className="flex justify-end pt-2">
        <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="btn-create-task">
          {busy ? 'Envoi…' : 'Créer et envoyer'}
        </Button>
      </div>
    </div>
  );
}

function TabButton({ id, current, setTab, label, badge, highlight }) {
  const active = current === id;
  return (
    <button
      onClick={() => setTab(id)}
      className={`pb-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
        active ? 'text-blue-600 border-blue-600' : 'text-slate-500 border-transparent hover:text-slate-700'
      }`}
      data-testid={`tab-${id}`}
    >
      {label}
      {typeof badge === 'number' && badge > 0 && (
        <span
          className={`text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${
            highlight ? 'bg-red-500 text-white' : 'bg-slate-200 text-slate-700'
          }`}
        >
          {badge}
        </span>
      )}
    </button>
  );
}

function CallButton({ onClick, title, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      data-testid={testid}
      className="w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600 transition-colors"
    >
      {children}
    </button>
  );
}

function QuickAction({ Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 p-2 rounded-lg hover:bg-slate-50 transition-colors"
    >
      <div className="w-10 h-10 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-[10px] text-slate-600 text-center leading-tight">{label}</span>
    </button>
  );
}
